import React, { useState } from 'react';
import { Mail, Phone, Send, Heart, MessageCircle, Clock } from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    honeypot: '' // Hidden field for spam protection
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Check if honeypot is filled (spam bot detection)
      if (formData.honeypot) {
        // Silently succeed to not alert the bot
        setSubmitStatus({
          type: 'success',
          message: '¡Gracias por tu mensaje! Te contactaremos pronto.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          honeypot: ''
        });
        setSubmitting(false);
        return;
      }

      const response = await fetch('/.netlify/functions/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: '¡Gracias por tu mensaje! Hemos recibido tu consulta y te contactaremos pronto a través de ' + formData.email
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          honeypot: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Error al enviar el mensaje. Por favor, intenta de nuevo.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Error de conexión. Por favor, verifica tu conexión a internet e intenta de nuevo.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div>
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-dark/90 via-primary/85 to-accent/90"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10 text-white">
          <Heart className="w-16 h-16 mx-auto mb-4 text-yellow-300" fill="currentColor" />
          <h1 className="text-6xl font-extrabold mb-4 drop-shadow-2xl">¡Hablemos! 💬</h1>
          <p className="text-2xl font-light drop-shadow-lg">Estamos aquí para hacer realidad tu sueño laboral</p>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-800 flex items-center gap-3">
                <MessageCircle className="w-10 h-10 text-accent" />
                Ponte en Contacto
              </h2>
              <p className="text-gray-700 text-lg mb-8 leading-relaxed">
                🚀 ¿Tienes preguntas sobre nuestros servicios? ¿Necesitas ayuda para encontrar trabajo en el extranjero?
                ¡No dudes en contactarnos! Nuestro equipo está listo para ayudarte a dar el siguiente paso.
              </p>

              <div className="space-y-4">
                <div className="flex items-center space-x-4 bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all border-l-4 border-accent">
                  <div className="bg-orange-100 p-3 rounded-full">
                    <Mail className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">Email</p>
                    <p className="text-gray-600">info@trabajofuera.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all border-l-4 border-primary">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">Teléfono</p>
                    <p className="text-gray-600">+34 678 601 100</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-2xl shadow-lg border-2 border-accent/30">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Clock className="w-7 h-7 text-accent" />
                  Horario de Atención
                </h3>
                <ul className="space-y-3 text-gray-700 text-lg">
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">📅</span>
                    <span><strong>Lunes - Jueves:</strong> 10:00 - 18:00</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">⏰</span>
                    <span><strong>Viernes:</strong> 10:00 - 15:00</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">😴</span>
                    <span><strong>Fines de Semana y Festivos:</strong> Cerrado</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-2xl border-t-4 border-accent">
              <h2 className="text-3xl font-bold mb-6 text-gray-800">¡Envíanos un Mensaje! ✉️</h2>

              {submitStatus.type && (
                <div className={`mb-6 p-4 rounded-xl ${
                  submitStatus.type === 'success'
                    ? 'bg-green-50 border-2 border-green-200 text-green-800'
                    : 'bg-red-50 border-2 border-red-200 text-red-800'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                    placeholder="+34 123 456 789"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Asunto *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                  >
                    <option value="">Selecciona un asunto</option>
                    <option value="Información General">Información General</option>
                    <option value="Búsqueda de Empleo">Búsqueda de Empleo</option>
                    <option value="Asesoría de Relocalización">Asesoría de Relocalización</option>
                    <option value="Empresas - Publicar Ofertas">Empresas - Publicar Ofertas</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-primary/20 rounded-xl focus:ring-2 focus:ring-accent focus:border-accent transition-all"
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                  />
                </div>

                {/* Honeypot field - hidden from users, visible to bots */}
                <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                  <label htmlFor="honeypot">Deja este campo vacío</label>
                  <input
                    type="text"
                    id="honeypot"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-accent hover:bg-accent-dark text-white px-6 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <Send className="w-6 h-6" />
                  <span>{submitting ? 'Enviando...' : '¡Enviar Mensaje!'}</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;